// iGaming Radar: casino brands with what open catalogs say about them, affiliate
// programs and their terms, and search demand by country. The data is a
// snapshot imported from the radar's own collectors (scripts/radar-import.mjs);
// each tab loads its dataset once and filters it in the browser.
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { getRadarMeta } from '@/api/radar';
import { BrandsTab } from './radar/BrandsTab';
import { AffiliatesTab } from './radar/AffiliatesTab';
import { DemandTab } from './radar/DemandTab';
import { ru } from './radar/logic';

const TABS = [
  { id: 'brands', label: 'Бренды' },
  { id: 'affiliates', label: 'Партнёрские программы' },
  { id: 'demand', label: 'Спрос по странам' },
] as const;
type Tab = (typeof TABS)[number]['id'];

export function Radar() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.some((t) => t.id === params.get('tab')) ? (params.get('tab') as Tab) : 'brands';
  const meta = useQuery({ queryKey: ['radar', 'meta'], queryFn: getRadarMeta, staleTime: 5 * 60_000 });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">iGaming Radar</h1>
        <p className="text-sm text-content-subtle mt-1">
          Каталог iGaming-брендов, партнёрские программы и поисковый спрос по странам из открытых источников.
          {meta.data && <> Снимок данных от {ru(meta.data.exported_at, { dateStyle: 'short' })}.</>}
        </p>
      </div>

      {meta.isSuccess && !meta.data ? (
        <div className="p-3 bg-warning-soft text-warning text-sm rounded">
          Снимок радара ещё не загружен (scripts/radar-import.mjs → R2 radar/).
        </div>
      ) : (
        <>
          <div className="flex gap-1 border-b border-border overflow-x-auto" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setParams(t.id === 'brands' ? {} : { tab: t.id }, { replace: true })}
                className={`px-4 py-2 text-sm whitespace-nowrap -mb-px border-b-2 ${tab === t.id ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === 'brands' && <BrandsTab />}
          {tab === 'affiliates' && <AffiliatesTab />}
          {tab === 'demand' && <DemandTab />}
        </>
      )}
    </div>
  );
}
